import React from "react";
import { toast } from "sonner";
import {
  FacebookLogo,
  InstagramLogo,
  TiktokLogo,
  LinkSimple,
} from "@phosphor-icons/react";

/**
 * Share strip that includes the product IMAGE, not just the link.
 * Strategy:
 *  - Facebook  → sharer.php (picks up og:image automatically)
 *  - IG/TikTok → if the browser supports Web Share Level 2 (files), share
 *    { title, text, url, files: [image] } so the mobile share sheet lets the
 *    user post directly into IG Stories / TikTok. Otherwise trigger a browser
 *    download of the image + copy the link + friendly toast.
 *  - Copy link → clipboard only.
 */
export default function ShareButtons({
  url,
  title = "Vape Essence",
  text = "",
  image,
}) {
  const finalUrl =
    url || (typeof window !== "undefined" ? window.location.href : "");
  const shareText = text || `Check out ${title} on Vape Essence`;

  const openWindow = (link) =>
    window.open(link, "_blank", "noopener,noreferrer,width=640,height=560");

  const shareFacebook = () => {
    openWindow(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(finalUrl)}`
    );
  };

  const copyLink = () => {
    navigator.clipboard.writeText(finalUrl);
    toast.success("Link copied");
  };

  // Fetch remote image and return a File instance suitable for Web Share L2.
  const fetchImageAsFile = async () => {
    if (!image) return null;
    try {
      const res = await fetch(image, { mode: "cors" });
      if (!res.ok) return null;
      const blob = await res.blob();
      const ext = (blob.type.split("/")[1] || "jpg").split(";")[0];
      const safeName = `${title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.${ext}`;
      return new File([blob], safeName, { type: blob.type });
    } catch {
      return null;
    }
  };

  const downloadImage = async () => {
    if (!image) return false;
    try {
      const file = await fetchImageAsFile();
      if (!file) return false;
      const dlUrl = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dlUrl);
      return true;
    } catch {
      return false;
    }
  };

  const sharePlatform = async (platformName) => {
    // 1. Try Web Share Level 2 with the image file (mobile only)
    const file = await fetchImageAsFile();
    if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title,
          text: `${shareText}\n${finalUrl}`,
          url: finalUrl,
          files: [file],
        });
        return;
      } catch {
        /* user cancelled — fall through to fallback */
      }
    }
    // 2. Fall back: try Web Share without file
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, text: shareText, url: finalUrl });
        return;
      } catch {
        /* cancelled */
        return;
      }
    }
    // 3. Desktop fallback: download image + copy link + helpful toast
    const ok = await downloadImage();
    navigator.clipboard.writeText(finalUrl);
    toast.success(ok ? "Image saved · link copied" : "Link copied", {
      description: ok
        ? `Post the image on ${platformName} with this link in your caption or bio.`
        : `Open ${platformName} and paste the link.`,
    });
  };

  return (
    <div data-testid="share-buttons" className="border border-white/10 bg-[#0d0d0d] p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="font-label text-[10px] tracking-widest text-white/50">SHARE</div>
      </div>
      <div className="flex flex-wrap gap-2">
        <ShareBtn
          onClick={shareFacebook}
          testId="share-facebook"
          label="Facebook"
          color="#1877F2"
        >
          <FacebookLogo size={14} weight="fill" />
        </ShareBtn>
        <ShareBtn
          onClick={() => sharePlatform("Instagram")}
          testId="share-instagram"
          label="Instagram"
          color="#E1306C"
        >
          <InstagramLogo size={14} weight="fill" />
        </ShareBtn>
        <ShareBtn
          onClick={() => sharePlatform("TikTok")}
          testId="share-tiktok"
          label="TikTok"
          color="#FFFFFF"
        >
          <TiktokLogo size={14} weight="fill" />
        </ShareBtn>
        <ShareBtn
          onClick={copyLink}
          testId="share-copy"
          label="Copy link"
          color="#00FFC2"
        >
          <LinkSimple size={13} weight="bold" />
        </ShareBtn>
      </div>
    </div>
  );
}

function ShareBtn({ onClick, testId, label, color, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-2 font-label text-[10px] hover:border-white/40 hover:-translate-y-0.5 transition-all"
      style={{ color }}
    >
      {children}
      <span className="text-white/80">{label}</span>
    </button>
  );
}
